import { type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { type Task } from "@/services/task.service";
import { type Project } from "@/services/project.service";
import {
	STATUS_BADGE,
	getStageLabel,
	getInitials,
	profileColorClass,
	formatTime,
} from "@/components/tasks/types";

export type ColumnKey =
	| "code"
	| "title"
	| "project"
	| "status"
	| "priority"
	| "assignee"
	| "sprint"
	| "ticket"
	| "created"
	| "due_date"
	| "tags"
	| "estimated_time"
	| "created_by";

export interface ColumnCtx {
	projects: Project[];
}

export interface ColumnDef {
	key: ColumnKey;
	label: string;
	cell: (task: Task, ctx: ColumnCtx) => ReactNode;
	csv: (task: Task, ctx: ColumnCtx) => string;
}

export interface ColumnState {
	key: ColumnKey;
	visible: boolean;
}

function projectName(task: Task, ctx: ColumnCtx): string {
	return ctx.projects.find((p) => p.id === task.project_id)?.name ?? "";
}

function personName(p: Task["assigned_to"]): string {
	return p ? (p.full_name ?? p.email) : "";
}

function dash(s: string): ReactNode {
	return s ? (
		<span className="text-xs text-muted-foreground">{s}</span>
	) : (
		<span className="text-xs text-muted">—</span>
	);
}

function statusBadge(status: string): ReactNode {
	const info = STATUS_BADGE[status];
	if (info) return <Badge variant={info.variant}>{info.label}</Badge>;
	return <Badge variant="todo">{getStageLabel(status)}</Badge>;
}

function personCell(p: Task["assigned_to"]): ReactNode {
	if (!p) return <span className="text-xs text-muted">—</span>;
	return (
		<div className="flex items-center gap-2">
			<Avatar className="h-6 w-6">
				{p.avatar_url && (
					<AvatarImage src={p.avatar_url} alt={p.full_name ?? p.email} />
				)}
				<AvatarFallback
					className={`text-[9px] text-white ${profileColorClass(p.id)}`}
				>
					{getInitials(p.full_name ?? p.email)}
				</AvatarFallback>
			</Avatar>
			<span className="text-xs text-muted-foreground">
				{p.full_name ?? p.email}
			</span>
		</div>
	);
}

export const COLUMNS: ColumnDef[] = [
	{
		key: "code",
		label: "Code",
		cell: (t) =>
			t.ticket?.ticket_code ? (
				<span className="text-xs font-medium text-muted-foreground">
					#{t.ticket.ticket_code}
				</span>
			) : (
				<span className="text-xs text-muted">—</span>
			),
		csv: (t) => (t.ticket?.ticket_code ? `#${t.ticket.ticket_code}` : ""),
	},
	{
		key: "title",
		label: "Title",
		cell: (t) => (
			<p className="text-sm font-medium text-foreground">{t.title}</p>
		),
		csv: (t) => t.title,
	},
	{
		key: "project",
		label: "Project",
		cell: (t, ctx) => dash(projectName(t, ctx)),
		csv: (t, ctx) => projectName(t, ctx),
	},
	{
		key: "status",
		label: "Status",
		cell: (t) => statusBadge(t.status),
		csv: (t) => getStageLabel(t.status),
	},
	{
		key: "priority",
		label: "Priority",
		cell: (t) => (
			<Badge variant={t.priority}>
				{t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}
			</Badge>
		),
		csv: (t) => t.priority,
	},
	{
		key: "assignee",
		label: "Assignee",
		cell: (t) => personCell(t.assigned_to),
		csv: (t) => personName(t.assigned_to),
	},
	{
		key: "sprint",
		label: "Sprint",
		cell: (t) => dash(t.sprint?.name ?? ""),
		csv: (t) => t.sprint?.name ?? "",
	},
	{
		key: "ticket",
		label: "Ticket",
		cell: (t) => dash(t.ticket?.ticket_code ?? ""),
		csv: (t) => t.ticket?.ticket_code ?? "",
	},
	{
		key: "created",
		label: "Created",
		cell: (t) => (
			<span className="text-xs text-muted">{t.created_at.slice(0, 10)}</span>
		),
		csv: (t) => t.created_at.slice(0, 10),
	},
	{
		key: "due_date",
		label: "Due",
		cell: (t) => (
			<span className="text-xs text-muted">
				{t.due_date ? t.due_date.slice(0, 10) : "—"}
			</span>
		),
		csv: (t) => (t.due_date ? t.due_date.slice(0, 10) : ""),
	},
	{
		key: "tags",
		label: "Tags",
		cell: (t) => dash((t.tags ?? []).join(", ")),
		csv: (t) => (t.tags ?? []).join(", "),
	},
	{
		key: "estimated_time",
		label: "Estimate",
		cell: (t) =>
			t.estimated_time ? (
				<span className="text-xs text-muted-foreground">
					{formatTime(t.estimated_time)}
				</span>
			) : (
				<span className="text-xs text-muted">—</span>
			),
		csv: (t) => (t.estimated_time ? formatTime(t.estimated_time) : ""),
	},
	{
		key: "created_by",
		label: "Created by",
		cell: (t) => personCell(t.created_by),
		csv: (t) => personName(t.created_by),
	},
];

export const COLUMN_MAP: Record<ColumnKey, ColumnDef> = Object.fromEntries(
	COLUMNS.map((c) => [c.key, c]),
) as Record<ColumnKey, ColumnDef>;

// Default visible set + order (matches the original static table).
const DEFAULT_VISIBLE: ColumnKey[] = [
	"title",
	"project",
	"status",
	"priority",
	"assignee",
	"sprint",
	"created",
];

export const DEFAULT_COLUMNS: ColumnState[] = COLUMNS.map((c) => ({
	key: c.key,
	visible: DEFAULT_VISIBLE.includes(c.key),
}));

// Drop unknown keys from stored prefs and append any newly-added registry
// columns (hidden) so future additions don't break saved layouts.
export function reconcileColumns(stored: ColumnState[]): ColumnState[] {
	const known = new Set(COLUMNS.map((c) => c.key));
	const seen = new Set<ColumnKey>();
	const result: ColumnState[] = [];
	for (const s of stored) {
		if (known.has(s.key) && !seen.has(s.key)) {
			result.push({ key: s.key, visible: !!s.visible });
			seen.add(s.key);
		}
	}
	for (const c of COLUMNS) {
		if (!seen.has(c.key)) result.push({ key: c.key, visible: false });
	}
	return result;
}
